// Contact Request API - Friend request system for 1-to-1 messaging
import { turso } from '@/database/client';

export interface ContactRequest {
  id: string;
  from_user_id: string;
  to_user_id: string;
  from_user_name: string;
  to_user_name: string;
  status: 'pending' | 'approved' | 'rejected';
  created_at: string;
  updated_at: string;
}

// Send a contact request
export async function sendContactRequest(fromUserId: string, toUserId: string, fromUserName: string, toUserName: string): Promise<boolean> {
  try {
    console.log('[ContactRequest] Sending request from', fromUserName, 'to', toUserName);
    
    // Check if request already exists
    const existing = await turso.execute({
      sql: `SELECT id, status FROM contact_requests WHERE from_user_id = ? AND to_user_id = ?`,
      args: [fromUserId, toUserId]
    });
    
    if (existing.rows.length > 0) {
      const status = existing.rows[0].status;
      if (status === 'pending') {
        console.log('[ContactRequest] Request already pending');
        return true;
      } else if (status === 'approved') {
        console.log('[ContactRequest] Already contacts');
        return true;
      } else {
        // Update rejected request to pending
        await turso.execute({
          sql: `UPDATE contact_requests SET status = 'pending', updated_at = datetime('now') WHERE id = ?`,
          args: [existing.rows[0].id]
        });
        console.log('[ContactRequest] ✅ Re-sent request');
        return true;
      }
    }
    
    const requestId = `cr_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    await turso.execute({
      sql: `INSERT INTO contact_requests (id, from_user_id, to_user_id, from_user_name, to_user_name, status) VALUES (?, ?, ?, ?, ?, 'pending')`,
      args: [requestId, fromUserId, toUserId, fromUserName, toUserName]
    });
    
    console.log('[ContactRequest] ✅ Request sent');
    return true;
  } catch (error) {
    console.error('[ContactRequest] Failed to send request:', error);
    return false;
  }
}

// Get pending requests sent TO you (you need to approve)
export async function getIncomingRequests(userId: string): Promise<ContactRequest[]> {
  try {
    const result = await turso.execute({
      sql: `SELECT * FROM contact_requests WHERE to_user_id = ? AND status = 'pending' ORDER BY created_at DESC`,
      args: [userId]
    });
    
    return result.rows.map((row: any) => ({
      id: row.id,
      from_user_id: row.from_user_id,
      to_user_id: row.to_user_id,
      from_user_name: row.from_user_name,
      to_user_name: row.to_user_name,
      status: row.status,
      created_at: row.created_at,
      updated_at: row.updated_at
    }));
  } catch (error) {
    console.error('[ContactRequest] Failed to get incoming requests:', error);
    return [];
  }
}

// Get pending requests sent BY you (waiting for approval)
export async function getOutgoingRequests(userId: string): Promise<ContactRequest[]> {
  try {
    const result = await turso.execute({
      sql: `SELECT * FROM contact_requests WHERE from_user_id = ? AND status = 'pending' ORDER BY created_at DESC`,
      args: [userId]
    });
    
    return result.rows.map((row: any) => ({
      id: row.id,
      from_user_id: row.from_user_id,
      to_user_id: row.to_user_id,
      from_user_name: row.from_user_name,
      to_user_name: row.to_user_name,
      status: row.status,
      created_at: row.created_at,
      updated_at: row.updated_at
    }));
  } catch (error) {
    console.error('[ContactRequest] Failed to get outgoing requests:', error);
    return [];
  }
}

// Approve a contact request
export async function approveContactRequest(requestId: string): Promise<boolean> {
  try {
    console.log('[ContactRequest] Approving request:', requestId);
    
    // Update request status
    await turso.execute({
      sql: `UPDATE contact_requests SET status = 'approved', updated_at = datetime('now') WHERE id = ?`,
      args: [requestId]
    });
    
    // Get request details to create reverse relationship
    const request = await turso.execute({
      sql: `SELECT * FROM contact_requests WHERE id = ?`,
      args: [requestId]
    });
    
    if (request.rows.length > 0) {
      const req: any = request.rows[0];
      
      // Create reverse relationship (bidirectional contact)
      const reverseId = `cr_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      await turso.execute({
        sql: `INSERT OR IGNORE INTO contact_requests (id, from_user_id, to_user_id, from_user_name, to_user_name, status) VALUES (?, ?, ?, ?, ?, 'approved')`,
        args: [reverseId, req.to_user_id, req.from_user_id, req.to_user_name, req.from_user_name]
      });
      
      console.log('[ContactRequest] ✅ Request approved (bidirectional)');
    }
    
    return true;
  } catch (error) {
    console.error('[ContactRequest] Failed to approve request:', error);
    return false;
  }
}

// Reject a contact request
export async function rejectContactRequest(requestId: string): Promise<boolean> {
  try {
    console.log('[ContactRequest] Rejecting request:', requestId);
    
    await turso.execute({
      sql: `UPDATE contact_requests SET status = 'rejected', updated_at = datetime('now') WHERE id = ?`,
      args: [requestId]
    });
    
    console.log('[ContactRequest] ✅ Request rejected');
    return true;
  } catch (error) {
    console.error('[ContactRequest] Failed to reject request:', error);
    return false;
  }
}

// Get approved contacts (your contact list)
export async function getApprovedContacts(userId: string): Promise<any[]> {
  try {
    const result = await turso.execute({
      sql: `
        SELECT DISTINCT 
          u.id, 
          u.name, 
          u.email,
          u.avatar,
          u.profile_color
        FROM contact_requests cr
        INNER JOIN users u ON u.id = cr.to_user_id
        WHERE cr.from_user_id = ? AND cr.status = 'approved'
        ORDER BY u.name ASC
      `,
      args: [userId]
    });
    
    return result.rows.map((row: any) => ({
      id: row.id,
      name: row.name,
      email: row.email,
      avatar: row.avatar,
      profile_color: row.profile_color
    }));
  } catch (error) {
    console.error('[ContactRequest] Failed to get approved contacts:', error);
    return [];
  }
}

// Get all users (for browsing)
export async function getAllUsers(currentUserId: string): Promise<any[]> {
  try {
    const result = await turso.execute({
      sql: `
        SELECT 
          u.id, 
          u.name, 
          u.email,
          u.avatar,
          u.profile_color,
          (SELECT status FROM contact_requests WHERE from_user_id = ? AND to_user_id = u.id) as request_status
        FROM users u
        WHERE u.id != ?
        ORDER BY u.name ASC
      `,
      args: [currentUserId, currentUserId]
    });
    
    return result.rows.map((row: any) => ({
      id: row.id,
      name: row.name,
      email: row.email,
      avatar: row.avatar,
      profile_color: row.profile_color,
      request_status: row.request_status || null
    }));
  } catch (error) {
    console.error('[ContactRequest] Failed to get all users:', error);
    return [];
  }
}

// Remove contact (mutual unfriend)
export async function removeContact(userId: string, contactId: string): Promise<boolean> {
  try {
    console.log('[ContactRequest] Removing contact:', userId, '↔', contactId);
    
    // Delete both directions
    await turso.execute({
      sql: `DELETE FROM contact_requests WHERE (from_user_id = ? AND to_user_id = ?) OR (from_user_id = ? AND to_user_id = ?)`,
      args: [userId, contactId, contactId, userId]
    });
    
    console.log('[ContactRequest] ✅ Contact removed');
    return true;
  } catch (error) {
    console.error('[ContactRequest] Failed to remove contact:', error);
    return false;
  }
}
